import { ZodType } from 'zod';

import { HttpService } from '@/data/http/http-service';
import {
  getImpactAnswersDtoSchema_BuildingHeating,
  QuestionBuildingHeatingDto,
} from '@/data/http/impact-co2/questions/building-heating/dto.type.ts';
import { QuestionBuildingHeatingMapper } from '@/data/http/impact-co2/questions/building-heating/mapper.ts';
import {
  getImpactAnswersDtoSchema_Cargo,
  QuestionCargoDto,
} from '@/data/http/impact-co2/questions/cargo/dto.type.ts';
import { QuestionCargoMapper } from '@/data/http/impact-co2/questions/cargo/mapper.ts';
import {
  getImpactAnswersDtoSchema_Digital,
  QuestionDigitalDto,
} from '@/data/http/impact-co2/questions/digital/dto.type.ts';
import { QuestionDigitalMapper } from '@/data/http/impact-co2/questions/digital/mapper.ts';
import {
  getImpactAnswersDtoSchema_OfficeSuppliesNew,
  QuestionOfficeSuppliesNewDto,
} from '@/data/http/impact-co2/questions/office-supplies-new/dto.type.ts';
import { QuestionOfficeSuppliesNewMapper } from '@/data/http/impact-co2/questions/office-supplies-new/mapper.ts';
import {
  getImpactAnswersDtoSchema_OfficeSuppliesRefurbish,
  QuestionOfficeSuppliesRefurbishDto,
} from '@/data/http/impact-co2/questions/office-supplies-refurbish/dto.type.ts';
import { QuestionOfficeSuppliesRefurbishMapper } from '@/data/http/impact-co2/questions/office-supplies-refurbish/mapper.ts';
import {
  getImpactAnswersDtoSchema_ScenographyNoRecyclable,
  QuestionScenographyNoRecyclableDto,
} from '@/data/http/impact-co2/questions/scenography-no-recyclable/dto.type.ts';
import { QuestionScenographyNoRecyclableMapper } from '@/data/http/impact-co2/questions/scenography-no-recyclable/mapper.ts';
import {
  getImpactAnswersDtoSchema_ScenographyRecyclable,
  QuestionScenographyRecyclableDto,
} from '@/data/http/impact-co2/questions/scenography-recyclable/dto.type.ts';
import { QuestionScenographyRecyclableMapper } from '@/data/http/impact-co2/questions/scenography-recyclable/mapper.ts';
import {
  getImpactAnswersDtoSchema_TeamMobility,
  QuestionTeamMobilityDto,
} from '@/data/http/impact-co2/questions/team-mobility/dto.type.ts';
import { QuestionTeamMobilityMapper } from '@/data/http/impact-co2/questions/team-mobility/mapper.ts';
import {
  Category,
  ImpactCo2Answer,
  ValuesAccommodation,
  ValuesAccommodationPublic,
  ValuesBuilding,
  ValuesBuildingHeating,
  ValuesCargo,
  ValuesCateringBeverage,
  ValuesCateringMeal,
  ValuesDigital,
  ValuesEnergy,
  ValuesEquipment,
  ValuesGenerator,
  ValuesMobility,
  ValuesMobilityPublic,
  ValuesNonRecyclableWaste,
  ValuesOfficeSuppliesNew,
  ValuesOfficeSuppliesRefurbish,
  ValuesRecyclableWaste,
  ValuesScenographyNoRecyclable,
  ValuesScenographyRecyclable,
  ValuesTeamMobility,
  ValuesWater,
} from '@/domain/impact-co2/entities/question.entity';

import {
  getImpactAnswersDtoSchema_Accommodation,
  QuestionAccommodationDto,
} from './accommodation/dto.type';
import { QuestionAccommodationMapper } from './accommodation/mapper';
import {
  getImpactAnswersDtoSchema_AccommodationPublic,
  QuestionAccommodationPublicDto,
} from './accommodation-public/dto.type';
import { QuestionAccommodationPublicMapper } from './accommodation-public/mapper';
import {
  getImpactAnswersDtoSchema_Building,
  QuestionBuildingDto,
} from './building/dto.type';
import { QuestionBuildingMapper } from './building/mapper';
import {
  getImpactAnswersDtoSchema_CateringBeverage,
  QuestionCateringBeverageDto,
} from './catering-beverage/dto.type';
import { QuestionCateringBeverageMapper } from './catering-beverage/mapper';
import {
  getImpactAnswersDtoSchema_CateringMeal,
  QuestionCateringMealDto,
} from './catering-meal/dto.type';
import { QuestionCateringMealMapper } from './catering-meal/mapper';
import {
  getImpactAnswersDtoSchema_Energy,
  QuestionEnergyDto,
} from './energy/dto.type';
import { QuestionEnergyMapper } from './energy/mapper';
import {
  getImpactAnswersDtoSchema_Equipment,
  QuestionEquipmentDto,
} from './equipment/dto.type';
import { QuestionEquipmentMapper } from './equipment/mapper';
import {
  getImpactAnswersDtoSchema_Generator,
  QuestionGeneratorDto,
} from './generator/dto.type';
import { QuestionGeneratorMapper } from './generator/mapper';
import {
  getImpactAnswersDtoSchema_Mobility,
  QuestionMobilityDto,
} from './mobility/dto.type';
import { QuestionMobilityMapper } from './mobility/mapper';
import {
  getImpactAnswersDtoSchema_MobilityPublic,
  QuestionMobilityPublicDto,
} from './mobility-public/dto.type';
import { QuestionMobilityPublicMapper } from './mobility-public/mapper';
import {
  getImpactAnswersDtoSchema_NonRecyclableWaste,
  QuestionNonRecyclableWasteDto,
} from './non-recyclable-waste/dto.type';
import { QuestionNonRecyclableWasteMapper } from './non-recyclable-waste/mapper';
import {
  getImpactAnswersDtoSchema_RecyclableWaste,
  QuestionRecyclableWasteDto,
} from './recyclable-waste/dto.type';
import { QuestionRecyclableWasteMapper } from './recyclable-waste/mapper';
import { buildUrl } from './utils/build-url-from-category';
import {
  getImpactAnswersDtoSchema_Water,
  QuestionWaterDto,
} from './water/dto.type';
import { QuestionWaterMapper } from './water/mapper';

const http = new HttpService({ baseURL: import.meta.env.VITE_API_IMPACT });

export async function getImpactAnswersForCategory(
  projectId: number,
  category: Category,
): Promise<ImpactCo2Answer[]> {
  switch (category) {
    case 'ACCOMMODATION':
      return await getImpactAnswersOfType<
        QuestionAccommodationDto,
        ValuesAccommodation
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_Accommodation,
        QuestionAccommodationMapper.dtoToEntity,
      );
    case 'ACCOMMODATION_PUBLIC':
      return await getImpactAnswersOfType<
        QuestionAccommodationPublicDto,
        ValuesAccommodationPublic
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_AccommodationPublic,
        QuestionAccommodationPublicMapper.dtoToEntity,
      );
    case 'BUILDING':
      return await getImpactAnswersOfType<QuestionBuildingDto, ValuesBuilding>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Building,
        QuestionBuildingMapper.dtoToEntity,
      );
    case 'CARGO':
      return await getImpactAnswersOfType<QuestionCargoDto, ValuesCargo>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Cargo,
        QuestionCargoMapper.dtoToEntity,
      );
    case 'CATERING_BEVERAGE':
      return await getImpactAnswersOfType<
        QuestionCateringBeverageDto,
        ValuesCateringBeverage
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_CateringBeverage,
        QuestionCateringBeverageMapper.dtoToEntity,
      );
    case 'CATERING_MEAL':
      return await getImpactAnswersOfType<
        QuestionCateringMealDto,
        ValuesCateringMeal
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_CateringMeal,
        QuestionCateringMealMapper.dtoToEntity,
      );
    case 'ENERGY':
      return await getImpactAnswersOfType<QuestionEnergyDto, ValuesEnergy>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Energy,
        QuestionEnergyMapper.dtoToEntity,
      );
    case 'EQUIPMENT':
      return await getImpactAnswersOfType<
        QuestionEquipmentDto,
        ValuesEquipment
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_Equipment,
        QuestionEquipmentMapper.dtoToEntity,
      );
    case 'GENERATOR':
      return await getImpactAnswersOfType<
        QuestionGeneratorDto,
        ValuesGenerator
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_Generator,
        QuestionGeneratorMapper.dtoToEntity,
      );
    case 'MOBILITY':
      return await getImpactAnswersOfType<QuestionMobilityDto, ValuesMobility>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Mobility,
        QuestionMobilityMapper.dtoToEntity,
      );
    case 'MOBILITY_PUBLIC':
      return await getImpactAnswersOfType<
        QuestionMobilityPublicDto,
        ValuesMobilityPublic
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_MobilityPublic,
        QuestionMobilityPublicMapper.dtoToEntity,
      );
    case 'NON_RECYCLABLE_WASTE':
      return await getImpactAnswersOfType<
        QuestionNonRecyclableWasteDto,
        ValuesNonRecyclableWaste
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_NonRecyclableWaste,
        QuestionNonRecyclableWasteMapper.dtoToEntity,
      );
    case 'RECYCLABLE_WASTE':
      return await getImpactAnswersOfType<
        QuestionRecyclableWasteDto,
        ValuesRecyclableWaste
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_RecyclableWaste,
        QuestionRecyclableWasteMapper.dtoToEntity,
      );
    case 'WATER':
      return await getImpactAnswersOfType<QuestionWaterDto, ValuesWater>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Water,
        QuestionWaterMapper.dtoToEntity,
      );
    case 'SCENOGRAPHY_NO_RECYCLABLE':
      return await getImpactAnswersOfType<
        QuestionScenographyNoRecyclableDto,
        ValuesScenographyNoRecyclable
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_ScenographyNoRecyclable,
        QuestionScenographyNoRecyclableMapper.dtoToEntity,
      );
    case 'SCENOGRAPHY_RECYCLABLE':
      return await getImpactAnswersOfType<
        QuestionScenographyRecyclableDto,
        ValuesScenographyRecyclable
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_ScenographyRecyclable,
        QuestionScenographyRecyclableMapper.dtoToEntity,
      );
    case 'BUILDING_HEATING':
      return await getImpactAnswersOfType<
        QuestionBuildingHeatingDto,
        ValuesBuildingHeating
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_BuildingHeating,
        QuestionBuildingHeatingMapper.dtoToEntity,
      );
    case 'OFFICE_SUPPLIES_NEW':
      return await getImpactAnswersOfType<
        QuestionOfficeSuppliesNewDto,
        ValuesOfficeSuppliesNew
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_OfficeSuppliesNew,
        QuestionOfficeSuppliesNewMapper.dtoToEntity,
      );
    case 'OFFICE_SUPPLIES_REFURBISH':
      return await getImpactAnswersOfType<
        QuestionOfficeSuppliesRefurbishDto,
        ValuesOfficeSuppliesRefurbish
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_OfficeSuppliesRefurbish,
        QuestionOfficeSuppliesRefurbishMapper.dtoToEntity,
      );
    case 'TEAM_MOBILITY':
      return await getImpactAnswersOfType<
        QuestionTeamMobilityDto,
        ValuesTeamMobility
      >(
        projectId,
        category,
        getImpactAnswersDtoSchema_TeamMobility,
        QuestionTeamMobilityMapper.dtoToEntity,
      );
    case 'DIGITAL':
      return await getImpactAnswersOfType<QuestionDigitalDto, ValuesDigital>(
        projectId,
        category,
        getImpactAnswersDtoSchema_Digital,
        QuestionDigitalMapper.dtoToEntity,
      );
    default:
      throw 'unknown category';
  }
}

async function getImpactAnswersOfType<TDto, TValues>(
  projectId: number,
  category: Category,
  dtoSchema: ZodType<TDto[]>,
  dtoToEntityMapper: (dto: TDto) => ImpactCo2Answer<TValues>,
): Promise<ImpactCo2Answer<TValues>[]> {
  const url = buildUrl(category);
  const options = { params: { projectId } };
  const response = await http.get(url, options);
  const result = dtoSchema.safeParse(response.data);
  if (result.success === false) {
    console.error(result.error);
    throw result.error;
  }
  const answers: ImpactCo2Answer<TValues>[] = result.data.map((x: TDto) =>
    dtoToEntityMapper(x),
  );
  return answers;
}
